import React, { useState, useEffect, useMemo, useRef } from 'react';
import './MediaManager.css';
import Overlays from '../Overlays';
import DefaultAds from '../DefaultAds/DefaultAds';
import useYouTubeConnection from '../../hooks/useYouTubeConnection';
import {
  listExternalAppProfiles,
  playExternalAppSeed,
  previewExternalMediaLink,
  saveExternalMediaLinkToArea,
  searchIptvCatalog,
  searchProviderContent,
} from '../Apps/externalAppControlApi';

function MediaManager({
  playlistName,
  onSavePlaylist,
  onAddToFallback,
  onLoadToUpNext,
  onUpdatePlaylist,
  onDeletePlaylist,
  onLoadSavedPlaylist,
  savedPlaylists = [],
  searchCategoryOptions = ['all', 'live', 'mix', 'playlists'],
  defaultCategory = 'all',
  limitSearchToLive = false,
  fallbackMode = false,
  existingPlaylist = [],
  adHocMode = false,
  deviceType = 'Music Player',
  categoryFixed = false,
  showLoadToUpNextButton = false,
  showPlaylist = true,
  siteId = null,
  site = null,
  selectedDevice = null,
  showOverlayAdsTab = false,
  overlayPlaybackMode = 'auto',
  onPlayInSuitestream = null
}) {
  function decodeHtmlEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [lastSearchMeta, setLastSearchMeta] = useState(null);
  const [playlist, setPlaylist] = useState(existingPlaylist);
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [selectedPlaylistItem, setSelectedPlaylistItem] = useState(null);
  const [selectedSearchResults, setSelectedSearchResults] = useState([]);
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState('Morning');
  const [selectedLength, setSelectedLength] = useState('any');
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [showPlaylistPopup, setShowPlaylistPopup] = useState(false);
  const [sourceTab, setSourceTab] = useState('youtube');
  const [selectedSavedPlaylistId, setSelectedSavedPlaylistId] = useState(null);
  const [savedAssets, setSavedAssets] = useState([]);
  const [savedAssetsLoading, setSavedAssetsLoading] = useState(false);
  const [selectedSavedAssets, setSelectedSavedAssets] = useState([]);
  const [savedAssetDurations, setSavedAssetDurations] = useState({});
  const [savedAssetTypeFilter, setSavedAssetTypeFilter] = useState('all');
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [youtubePlaylistsOpen, setYoutubePlaylistsOpen] = useState(false);
  const [youtubePlaylists, setYoutubePlaylists] = useState([]);
  const [youtubePlaylistsLoading, setYoutubePlaylistsLoading] = useState(false);
  const [youtubePlaylistsError, setYoutubePlaylistsError] = useState('');
  const [selectedYouTubePlaylistId, setSelectedYouTubePlaylistId] = useState(null);
  const [selectedChannelPlaylistId, setSelectedChannelPlaylistId] = useState(null);
  const [channelVideos, setChannelVideos] = useState([]);
  const [channelPlaylists, setChannelPlaylists] = useState([]);
  const [channelMeta, setChannelMeta] = useState(null);
  const [channelResultsTab, setChannelResultsTab] = useState('videos');
  const [channelVideosNextToken, setChannelVideosNextToken] = useState(null);
  const [channelPlaylistsNextToken, setChannelPlaylistsNextToken] = useState(null);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelError, setChannelError] = useState('');
  const [youtubeDeviceProfile, setYoutubeDeviceProfile] = useState(null);
  const [externalAppProfiles, setExternalAppProfiles] = useState([]);
  const [youtubeTargetBusy, setYoutubeTargetBusy] = useState('');
  const [youtubeTargetMessage, setYoutubeTargetMessage] = useState('');
  const [externalLinkInput, setExternalLinkInput] = useState('');
  const [externalLinkPreview, setExternalLinkPreview] = useState(null);
  const [externalLinkBusy, setExternalLinkBusy] = useState('');
  const [externalLinkMessage, setExternalLinkMessage] = useState('');
  const [externalLinkHistory, setExternalLinkHistory] = useState([]);
  const [searchProvider, setSearchProvider] = useState('youtube');
  const [providerSearchResults, setProviderSearchResults] = useState([]);
  const [providerSearchMessage, setProviderSearchMessage] = useState('');
  const [providerSearchFallbackUrl, setProviderSearchFallbackUrl] = useState('');
  const [externalLinkProfileId, setExternalLinkProfileId] = useState('');
  const [iptvQuery, setIptvQuery] = useState('');
  const [iptvCountry, setIptvCountry] = useState('VN');
  const [iptvCategory, setIptvCategory] = useState('');
  const [iptvResults, setIptvResults] = useState([]);
  const [iptvCountries, setIptvCountries] = useState([]);
  const [iptvCategories, setIptvCategories] = useState([]);
  const [iptvTotal, setIptvTotal] = useState(0);
  const [iptvPage, setIptvPage] = useState(1);
  const [iptvHasMore, setIptvHasMore] = useState(false);
  const [iptvLoading, setIptvLoading] = useState(false);
  const [iptvMessage, setIptvMessage] = useState('');
  const [iptvBusy, setIptvBusy] = useState('');
  const iptvRequestId = useRef(0);
  const { status: youtubeStatus } = useYouTubeConnection(siteId);

  const CONTENT_TYPE_META = {
    youtube: { label: 'YouTube', badgeClass: 'badge-youtube' },
    s3_video: { label: 'Saved Video', badgeClass: 'badge-video' },
    s3_image: { label: 'Saved Image', badgeClass: 'badge-image' },
    s3_audio: { label: 'Saved Audio', badgeClass: 'badge-audio' },
  };
  const SAVED_ASSET_TYPE_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 's3_image', label: 'Images' },
    { value: 's3_audio', label: 'Audio' },
    { value: 's3_video', label: 'Video' },
    { value: 'other', label: 'Etc' },
  ];

  const resolveContentTypeMeta = (item = {}) => {
    const type = (item.content_type || 'youtube').toLowerCase();
    const meta = CONTENT_TYPE_META[type];
    if (meta) return meta;
    const printable = type.replace(/[_-]/g, ' ').trim() || 'External';
    const label = printable.charAt(0).toUpperCase() + printable.slice(1);
    return { label, badgeClass: 'badge-external' };
  };

  useEffect(() => {
    if (JSON.stringify(existingPlaylist) !== JSON.stringify(playlist)) {
      setPlaylist(existingPlaylist);
    }
  }, [existingPlaylist]);

  useEffect(() => {
    let cancelled = false;
    const deviceId = selectedDevice?.id || selectedDevice?.device_id;
    setYoutubeDeviceProfile(null);
    setExternalAppProfiles([]);
    setYoutubeTargetMessage('');
    if (!deviceId) return undefined;
    listExternalAppProfiles(deviceId)
      .then((profiles) => {
        if (cancelled) return;
        setExternalAppProfiles(profiles);
        const profile = profiles.find((entry) => (
          entry.adapterKey === 'youtube'
          || /^com\.google\.android\.youtube(?:\.tv)?$/i.test(entry.packageName || '')
        ));
        setYoutubeDeviceProfile(profile || null);
      })
      .catch(() => {
        if (!cancelled) {
          setYoutubeDeviceProfile(null);
          setExternalAppProfiles([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDevice?.id, selectedDevice?.device_id]);

  const externalLinkHistoryKey = useMemo(() => {
    const deviceId = selectedDevice?.id || selectedDevice?.device_id || 'none';
    return `soundscreen:external-link-history:${siteId || 'none'}:${deviceId}`;
  }, [siteId, selectedDevice?.id, selectedDevice?.device_id]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(externalLinkHistoryKey) || '[]');
      setExternalLinkHistory(Array.isArray(stored) ? stored.slice(0, 20) : []);
    } catch {
      setExternalLinkHistory([]);
    }
  }, [externalLinkHistoryKey]);

  const storeExternalLinkHistory = (history) => {
    const nextHistory = history.slice(0, 20);
    setExternalLinkHistory(nextHistory);
    localStorage.setItem(externalLinkHistoryKey, JSON.stringify(nextHistory));
  };

  const rememberExternalLink = (preview) => {
    if (!preview?.canonicalUrl) return;
    const entry = {
      url: preview.canonicalUrl,
      title: preview.title,
      provider: preview.provider,
      providerKind: preview.providerKind,
      checkedAt: new Date().toISOString(),
    };
    storeExternalLinkHistory([
      entry,
      ...externalLinkHistory.filter((item) => item.url !== entry.url),
    ]);
  };

  const reuseExternalLink = (entry) => {
    setExternalLinkInput(entry.url);
    setExternalLinkPreview({
      canonicalUrl: entry.url,
      title: entry.title,
      provider: entry.provider,
      providerKind: entry.providerKind,
    });
    setExternalLinkProfileId('');
    setExternalLinkMessage('');
  };

  const deleteExternalLinkHistoryEntry = (url) => {
    storeExternalLinkHistory(externalLinkHistory.filter((entry) => entry.url !== url));
    if (externalLinkPreview?.canonicalUrl === url) {
      setExternalLinkPreview(null);
    }
  };

  const saveYouTubeTarget = async ({ contentId, title, kind, execute }) => {
    if (!youtubeDeviceProfile || !contentId) return;
    const busyKey = `${kind}:${contentId}:${execute ? 'play' : 'save'}`;
    setYoutubeTargetBusy(busyKey);
    setYoutubeTargetMessage('');
    try {
      const payload = {
        provider: 'youtube',
        contentId,
        title: decodeHtmlEntities(title || ''),
        kind,
      };
      if (execute) {
        const response = await playExternalAppSeed(youtubeDeviceProfile.id, payload);
        const outcome = response.execution?.result?.outcome;
        setYoutubeTargetMessage(
          outcome === 'verified'
            ? `Opened ${payload.title} on the device.`
            : `Sent ${payload.title} to the installed app; playback could not be verified.`
        );
      } else {
        const response = await saveExternalMediaLinkToArea(youtubeDeviceProfile.id, payload);
        setYoutubeTargetMessage(
          response.created
            ? `Saved ${response.name} to ${response.compatibleDeviceCount} compatible screen${response.compatibleDeviceCount === 1 ? '' : 's'} in this area.`
            : `${response.name} was already saved across this area.`
        );
      }
    } catch (error) {
      setYoutubeTargetMessage(
        error.response?.data?.error || error.message || 'YouTube target action failed.'
      );
    } finally {
      setYoutubeTargetBusy('');
    }
  };

  const youtubeSeedLaunchUnsupported = Boolean(
    youtubeDeviceProfile?.capabilities?.launchUri === false
    || youtubeDeviceProfile?.capabilities?.bridge?.launchUri === false
  );

  const externalLinkProfiles = externalLinkPreview
    ? externalAppProfiles.filter((profile) => (
        profile.adapterKey === externalLinkPreview.provider
        || (
          externalLinkPreview.provider === 'direct_media'
          && ['vlc', 'kodi'].includes(profile.adapterKey)
        )
      ))
    : [];
  const externalLinkProfile = externalLinkProfiles.find(
    (profile) => String(profile.id) === String(externalLinkProfileId)
  ) || externalLinkProfiles[0] || null;

  const providerSearchOptions = useMemo(() => {
    const keys = new Set(externalAppProfiles.map((profile) => profile.adapterKey));
    const options = [{ key: 'youtube', label: 'YouTube' }];
    [
      ['spotify', 'Spotify'],
      ['soundcloud', 'SoundCloud'],
      ['twitch', 'Twitch'],
      ['tunein', 'TuneIn'],
    ].forEach(([key, label]) => {
      if (keys.has(key)) options.push({ key, label });
    });
    if (keys.has('vlc') || keys.has('kodi')) {
      options.push(
        { key: 'radio', label: 'Internet radio' },
        { key: 'webcams', label: 'Webcams & surf cams' }
      );
    }
    return options;
  }, [externalAppProfiles]);
  const iptvProfiles = externalAppProfiles.filter((profile) => (
    ['vlc', 'kodi'].includes(profile.adapterKey)
  ));
  const iptvAvailable = iptvProfiles.length > 0;

  useEffect(() => {
    if (!providerSearchOptions.some((option) => option.key === searchProvider)) {
      setSearchProvider('youtube');
    }
  }, [providerSearchOptions, searchProvider]);

  const previewExternalLink = async () => {
    setExternalLinkBusy('preview');
    setExternalLinkMessage('');
    setExternalLinkPreview(null);
    try {
      const preview = await previewExternalMediaLink(externalLinkInput);
      setExternalLinkPreview(preview);
      setExternalLinkProfileId('');
      rememberExternalLink(preview);
      if (!externalAppProfiles.some((profile) => profile.adapterKey === preview.provider)) {
        setExternalLinkMessage(
          `Configure the installed ${preview.provider} app in Apps before playing or saving this link.`
        );
      }
    } catch (error) {
      setExternalLinkMessage(
        error.response?.data?.error || error.message || 'This media link is not supported.'
      );
    } finally {
      setExternalLinkBusy('');
    }
  };

  const useProviderSearchResult = async (result) => {
    setExternalLinkInput(result.url);
    setExternalLinkBusy('preview');
    setExternalLinkMessage('');
    try {
      const preview = await previewExternalMediaLink(result.url);
      setExternalLinkPreview({
        ...preview,
        title: result.title || preview.title,
      });
      setExternalLinkProfileId('');
      rememberExternalLink({
        ...preview,
        title: result.title || preview.title,
      });
      setSourceTab('links');
    } catch (error) {
      setProviderSearchMessage(
        error.response?.data?.error
        || error.message
        || 'This result is a web page, not a directly playable media link.'
      );
    } finally {
      setExternalLinkBusy('');
    }
  };

  useEffect(() => () => { iptvRequestId.current += 1; }, []);

  const changeIptvFilter = (setFilter, value) => {
    setFilter(value);
    // A previous country's response must not replace the newly selected filters.
    iptvRequestId.current += 1;
    setIptvLoading(false);
    setIptvResults([]);
    setIptvTotal(0);
    setIptvPage(1);
    setIptvHasMore(false);
    setIptvMessage('');
  };

  const loadIptv = async (page = 1, append = false) => {
    const requestId = ++iptvRequestId.current;
    setIptvLoading(true);
    setIptvMessage('');
    try {
      const response = await searchIptvCatalog({
        query: iptvQuery.trim(),
        country: iptvCountry,
        category: iptvCategory,
        page,
      });
      if (requestId !== iptvRequestId.current) return;
      const nextResults = Array.isArray(response?.results) ? response.results : [];
      setIptvResults((current) => (append ? [...current, ...nextResults] : nextResults));
      setIptvCountries(Array.isArray(response?.countries) ? response.countries : []);
      setIptvCategories(Array.isArray(response?.categories) ? response.categories : []);
      setIptvTotal(Number(response?.total) || 0);
      setIptvPage(Number(response?.page) || page);
      setIptvHasMore(Boolean(response?.hasMore));
      if (!append && !nextResults.length) setIptvMessage('No matching IPTV streams found.');
    } catch (error) {
      if (requestId !== iptvRequestId.current) return;
      setIptvMessage(
        error.code === 'ECONNABORTED'
          ? 'Channel browsing timed out. Please press Browse to try again.'
          : error.response?.data?.error || error.message || 'IPTV catalogue is unavailable.'
      );
    } finally {
      if (requestId === iptvRequestId.current) setIptvLoading(false);
    }
  };

  const playIptvStream = async (stream, profile) => {
    if (!stream?.url || !profile) return;
    const busyKey = `${profile.id}:${stream.id}`;
    setIptvBusy(busyKey);
    setIptvMessage('');
    try {
      const response = await playExternalAppSeed(profile.id, {
        url: stream.url,
        title: stream.title,
      });
      rememberExternalLink({
        canonicalUrl: stream.url,
        title: stream.title,
        provider: 'direct_media',
        providerKind: 'network_stream',
      });
      setIptvMessage(
        response.execution?.result?.outcome === 'verified'
          ? `Opened ${stream.title} in ${profile.displayName || profile.adapterKey}.`
          : `Sent ${stream.title} to ${profile.displayName || profile.adapterKey}.`
      );
    } catch (error) {
      setIptvMessage(
        error.response?.data?.error || error.message || 'IPTV stream launch failed.'
      );
    } finally {
      setIptvBusy('');
    }
  };

  const handleExternalLinkAction = async (action) => {
    if (!externalLinkProfile || !externalLinkPreview) return;
    setExternalLinkBusy(action);
    setExternalLinkMessage('');
    try {
      const payload = {
        url: externalLinkPreview.canonicalUrl,
        title: externalLinkPreview.title,
      };
      if (action === 'play') {
        const response = await playExternalAppSeed(externalLinkProfile.id, payload);
        setExternalLinkMessage(
          response.execution?.result?.outcome === 'verified'
            ? `Opened ${externalLinkPreview.title} on the device.`
            : `Sent ${externalLinkPreview.title} to the installed app; playback could not be verified.`
        );
      } else {
        const response = await saveExternalMediaLinkToArea(externalLinkProfile.id, payload);
        setExternalLinkMessage(
          response.created
            ? `Saved ${response.name} to ${response.compatibleDeviceCount} compatible screen${response.compatibleDeviceCount === 1 ? '' : 's'} in this area.`
            : `${response.name} was already saved across this area.`
        );
      }
    } catch (error) {
      setExternalLinkMessage(
        error.response?.data?.error || error.message || 'External media action failed.'
      );
    } finally {
      setExternalLinkBusy('');
    }
  };

  const addExternalLinkToAdHoc = async () => {
    if (
      !adHocMode
      || !onUpdatePlaylist
      || externalLinkPreview?.provider !== 'youtube'
      || !externalLinkPreview.queueSupported
    ) return;
    setExternalLinkBusy('ad_hoc');
    setExternalLinkMessage('');
    try {
      const playlistResult = externalLinkPreview.kind === 'playlist'
        ? await loadYouTubePlaylistItems(externalLinkPreview.contentId)
        : null;
      const newItems = playlistResult?.items || [{
            content_id: externalLinkPreview.contentId,
            content_type: 'youtube',
            title: externalLinkPreview.title,
            thumbnail_url:
              externalLinkPreview.thumbnailUrl
              || `https://i.ytimg.com/vi/${externalLinkPreview.contentId}/hqdefault.jpg`,
            duration: null,
          }];
      if (!newItems.length) throw new Error('This link has no playable videos.');
      const updated = [...playlist, ...newItems];
      setPlaylist(updated);
      const saved = await onUpdatePlaylist(updated);
      if (saved === false) {
        setPlaylist(playlist);
        throw new Error('Could not save the updated ad hoc playlist.');
      }
      setExternalLinkMessage(
        `Added ${newItems.length} playable item${newItems.length === 1 ? '' : 's'} to the ad hoc playlist.${playlistResult?.skippedCount ? ` Skipped ${playlistResult.skippedCount} unavailable item${playlistResult.skippedCount === 1 ? '' : 's'}.` : ''}`
      );
    } catch (error) {
      setExternalLinkMessage(error.message || 'Could not add this link to the ad hoc playlist.');
    } finally {
      setExternalLinkBusy('');
    }
  };

  const getYouTubeVideoId = (video) => (
    video?.id?.videoId || video?.videoId || (typeof video?.id === 'string' ? video.id : '')
  );

  const getYouTubePlaylistId = (item) => (
    item?.id?.playlistId || item?.playlistId || (typeof item?.id === 'string' ? item.id : '')
  );

  const toSuitestreamVideo = (video) => ({
    content_id: getYouTubeVideoId(video),
    content_type: 'youtube',
    title: decodeHtmlEntities(video?.snippet?.title || video?.title || ''),
    thumbnail_url:
      video?.snippet?.thumbnails?.default?.url
      || video?.preview_api_url
      || video?.thumbnail_url
      || null,
  });

  const loadYouTubePlaylistItems = async (playlistId) => {
    if (!siteId || !playlistId) return { items: [], skippedCount: 0, totalCount: 0 };
    const token = localStorage.getItem('token');
    if (!token) return { items: [], skippedCount: 0, totalCount: 0 };
    const response = await fetch(
      `/api/youtube/playlist-items?site=${encodeURIComponent(siteId)}&playlistId=${encodeURIComponent(playlistId)}`,
      { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.error || 'Failed to load playlist items');
    }
    const data = await response.json();
    return {
      items: (Array.isArray(data.items) ? data.items : []).map((item) => ({
        content_id: item.videoId,
        content_type: 'youtube',
        title: decodeHtmlEntities(item.title),
        thumbnail_url: item.preview_api_url || item.thumbnail_url || null,
      })),
      skippedCount: Number(data.skippedCount) || 0,
      totalCount: Number(data.totalCount) || 0,
    };
  };

  const addYouTubeTargetToPlaylist = async ({ contentId, title, kind, video = null }) => {
    if (!onUpdatePlaylist || !contentId) return;
    setYoutubeTargetBusy(`playlist:${contentId}`);
    setYoutubeTargetMessage('');
    try {
      const playlistResult = kind === 'playlist'
        ? await loadYouTubePlaylistItems(contentId)
        : null;
      const newItems = playlistResult?.items || [video ? toSuitestreamVideo(video) : {
            content_id: contentId,
            content_type: 'youtube',
            title: decodeHtmlEntities(title || ''),
            thumbnail_url: `https://i.ytimg.com/vi/${contentId}/hqdefault.jpg`,
          }];
      if (!newItems.length) throw new Error('This playlist has no playable videos.');
      const updated = [...playlist, ...newItems];
      setPlaylist(updated);
      const saved = await onUpdatePlaylist(updated);
      if (saved === false) {
        setPlaylist(playlist);
        throw new Error('Could not save the updated ad hoc playlist.');
      }
      setYoutubeTargetMessage(
        `Added ${newItems.length} playable item${newItems.length === 1 ? '' : 's'} to ${adHocMode ? 'the ad hoc playlist' : 'the playlist'}.${playlistResult?.skippedCount ? ` Skipped ${playlistResult.skippedCount} unavailable item${playlistResult.skippedCount === 1 ? '' : 's'}.` : ''}`
      );
    } catch (error) {
      setYoutubeTargetMessage(error.message || 'Failed to add this YouTube content.');
    } finally {
      setYoutubeTargetBusy('');
    }
  };

  const playVideoInSuitestream = (video) => {
    const item = toSuitestreamVideo(video);
    if (!item.content_id || !onPlayInSuitestream) return;
    onPlayInSuitestream(item);
  };

  const playPlaylistInSuitestream = async (playlistId) => {
    if (!onPlayInSuitestream) return;
    setYoutubeTargetBusy(`suitestream:${playlistId}`);
    setYoutubeTargetMessage('');
    try {
      const { items, skippedCount } = await loadYouTubePlaylistItems(playlistId);
      if (!items.length) throw new Error('This playlist has no playable videos.');
      onPlayInSuitestream(items[0], items.slice(1));
      setYoutubeTargetMessage(`Opening the first video in Suitestream; ${items.length - 1} queued.${skippedCount ? ` Skipped ${skippedCount} unavailable item${skippedCount === 1 ? '' : 's'}.` : ''}`);
    } catch (error) {
      setYoutubeTargetMessage(error.message || 'Failed to play this playlist in Suitestream.');
    } finally {
      setYoutubeTargetBusy('');
    }
  };

  const categoryOptions = useMemo(() => {
    if (searchCategoryOptions.includes('channel')) {
      return searchCategoryOptions;
    }
    const options = [...searchCategoryOptions];
    if (options.includes('all') && options.includes('live')) {
      const liveIndex = options.indexOf('live');
      options.splice(liveIndex, 0, 'channel');
      return options;
    }
    options.push('channel');
    return options;
  }, [searchCategoryOptions]);

  useEffect(() => {
    if (!youtubePlaylistsOpen) return;
    if (!youtubeStatus?.connected) return;
    if (!siteId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelled = false;
    const controller = new AbortController();

    const loadPlaylists = async () => {
      setYoutubePlaylistsLoading(true);
      setYoutubePlaylistsError('');
      try {
        const response = await fetch(
          `/api/youtube/playlists?site=${encodeURIComponent(siteId)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.error || 'Failed to load playlists');
        }
        const data = await response.json();
        if (cancelled) return;
        setYoutubePlaylists(Array.isArray(data.items) ? data.items : []);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (cancelled) return;
        setYoutubePlaylists([]);
        setYoutubePlaylistsError(error.message || 'Failed to load playlists');
      } finally {
        if (!cancelled) {
          setYoutubePlaylistsLoading(false);
        }
      }
    };

    loadPlaylists();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [youtubePlaylistsOpen, youtubeStatus, siteId]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchSavedAssets = async () => {
      setSavedAssetsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const query = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
        const response = await fetch(`/api/creative/assets/saved${query}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to fetch saved assets');
        }
        const data = await response.json();
        const assets = data.assets || [];
        if (!isMounted) return;
        setSavedAssets(assets);
        setSavedAssetDurations(() => {
          const next = {};
          assets.forEach((asset) => {
            if (asset.content_type === 's3_image') {
              next[asset.id] = String(asset.default_duration || 30);
            }
          });
          return next;
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching saved assets:', error);
        if (!isMounted) return;
      } finally {
        if (isMounted) {
          setSavedAssetsLoading(false);
        }
      }
    };

    setSelectedSavedAssets([]);
    fetchSavedAssets();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [siteId]);

  useEffect(() => {
    if (!showOverlayAdsTab && sourceTab === 'overlay-ads') {
      setSourceTab('youtube');
    }
  }, [showOverlayAdsTab, sourceTab]);

  useEffect(() => {
    setSelectedSavedPlaylistId(null);
  }, [savedPlaylists]);

  useEffect(() => {
    if (selectedCategory === 'channel') return;
    setChannelMeta(null);
    setChannelVideos([]);
    setChannelPlaylists([]);
    setChannelVideosNextToken(null);
    setChannelPlaylistsNextToken(null);
    setChannelError('');
    setSelectedChannelPlaylistId(null);
    setChannelResultsTab('videos');
  }, [selectedCategory]);

  const resolveChannel = async (term) => {
    if (!siteId) return null;
    const token = localStorage.getItem('token');
    if (!token) return null;
    const response = await fetch(
      `/api/youtube/channel?site=${encodeURIComponent(siteId)}&query=${encodeURIComponent(term)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.error || 'Failed to resolve channel');
    }
    return response.json();
  };

  const fetchChannelContent = async ({ channelId, kind, pageToken }) => {
    if (!siteId) return { items: [], nextPageToken: null };
    const token = localStorage.getItem('token');
    if (!token) return { items: [], nextPageToken: null };
    const params = new URLSearchParams({
      site: siteId,
      channelId,
      kind,
    });
    if (pageToken) {
      params.set('pageToken', pageToken);
    }
    const response = await fetch(`/api/youtube/channel-content?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.error || 'Failed to load channel content');
    }
    return response.json();
  };

  const loadMoreChannelResults = async (kind) => {
    if (!channelMeta?.channelId) return;
    const nextToken = kind === 'videos' ? channelVideosNextToken : channelPlaylistsNextToken;
    if (!nextToken) return;
    setChannelLoading(true);
    setChannelError('');
    try {
      const data = await fetchChannelContent({
        channelId: channelMeta.channelId,
        kind,
        pageToken: nextToken,
      });
      if (kind === 'videos') {
        setChannelVideos((prev) => [...prev, ...(data.items || [])]);
        setChannelVideosNextToken(data.nextPageToken || null);
      } else {
        setChannelPlaylists((prev) => [...prev, ...(data.items || [])]);
        setChannelPlaylistsNextToken(data.nextPageToken || null);
      }
    } catch (error) {
      setChannelError(error.message || 'Failed to load channel results.');
    } finally {
      setChannelLoading(false);
    }
  };

  const handleSearch = async () => {
    const trimmedTerm = searchTerm.trim();
    if (!trimmedTerm) return;
    setSelectedSearchResults([]);

    let startedAt = null;
    let startedIso = null;

    try {
      if (searchProvider !== 'youtube') {
        setProviderSearchMessage('');
        setProviderSearchResults([]);
        setProviderSearchFallbackUrl('');
        const response = await searchProviderContent(searchProvider, trimmedTerm);
        setProviderSearchResults(Array.isArray(response?.results) ? response.results : []);
        setProviderSearchFallbackUrl(response?.fallbackUrl || '');
        setProviderSearchMessage(
          response?.unavailableReason
          || (!response?.results?.length ? 'No matching results found.' : '')
        );
        return;
      }
      if (selectedCategory === 'channel') {
        setChannelLoading(true);
        setChannelError('');
        setChannelMeta(null);
        setChannelVideos([]);
        setChannelPlaylists([]);
        setChannelVideosNextToken(null);
        setChannelPlaylistsNextToken(null);
        setSelectedSearchResults([]);
        setSelectedChannelPlaylistId(null);

        const channelData = await resolveChannel(trimmedTerm);
        if (!channelData?.channelId) {
          throw new Error('Channel not found');
        }
        setChannelMeta(channelData);
        const [videosRes, playlistsRes] = await Promise.all([
          fetchChannelContent({ channelId: channelData.channelId, kind: 'videos' }),
          fetchChannelContent({ channelId: channelData.channelId, kind: 'playlists' }),
        ]);
        setChannelVideos(Array.isArray(videosRes.items) ? videosRes.items : []);
        setChannelPlaylists(Array.isArray(playlistsRes.items) ? playlistsRes.items : []);
        setChannelVideosNextToken(videosRes.nextPageToken || null);
        setChannelPlaylistsNextToken(playlistsRes.nextPageToken || null);
        setChannelResultsTab('videos');
        return;
      }

      const params = {
        part: 'snippet',
        q: trimmedTerm,
        maxResults: 50,
        type: selectedCategory === 'playlists' ? 'playlist' : 'video',
      };
      if (!youtubeStatus?.connected && selectedCategory !== 'playlists') {
        params.videoEmbeddable = 'true';
        params.videoSyndicated = 'true';
      }
      if (siteId) {
        params.site = siteId;
      }

      if (limitSearchToLive || selectedCategory === 'live') {
        params.eventType = 'live';
      }

      if (selectedCategory === 'mix') {
        params.q += ' mix';
      }

      if (selectedCategory !== 'playlists' && selectedLength !== 'any') {
        params.videoDuration = selectedLength;
      }

      startedAt = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      startedIso = new Date().toISOString();
      setLastSearchMeta({
        term: trimmedTerm,
        startedAt,
        startedIso,
        status: 'in-flight',
      });
      console.log('[Dashboard] YouTube search start', {
        term: trimmedTerm,
        params,
        startedAt: startedIso,
      });

      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/youtube/search?` + new URLSearchParams(params),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      const results = Array.isArray(data) ? data : data?.items || [];
      const receivedAt = new Date();
      const elapsedMs =
        (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - startedAt;
      console.log('[Dashboard] YouTube search response', {
        term: trimmedTerm,
        count: results.length,
        elapsedMs,
        receivedAt: receivedAt.toISOString(),
      });
      setLastSearchMeta({
        term: trimmedTerm,
        startedAt,
        startedIso,
        receivedAt: receivedAt.getTime(),
        elapsedMs,
        status: 'received',
        resultCount: results.length,
      });
      setSearchResults(results);
    } catch (error) {
      if (selectedCategory === 'channel') {
        setChannelError(error.message || 'Failed to load channel results.');
      }
      const failedAt = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      console.error('[Dashboard] Error fetching search results', {
        term: trimmedTerm,
        error: error.response?.data || error.message || error,
        elapsedMs: startedAt ? failedAt - startedAt : undefined,
      });
      setLastSearchMeta((prev) => ({
        term: trimmedTerm,
        startedAt: startedAt || prev?.startedAt || failedAt,
        startedIso: startedIso || prev?.startedIso || new Date(failedAt).toISOString(),
        status: 'error',
        error: error.response?.data || error.message || error,
        elapsedMs: startedAt ? failedAt - startedAt : null,
      }));
      if (selectedCategory !== 'channel') {
        alert('Failed to fetch search results.');
      }
    } finally {
      if (selectedCategory === 'channel') {
        setChannelLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!lastSearchMeta || lastSearchMeta.status !== 'received') return;
    const now = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    const elapsedFromStart = lastSearchMeta.startedAt ? now - lastSearchMeta.startedAt : null;
    const renderLatency = lastSearchMeta.receivedAt != null ? now - lastSearchMeta.receivedAt : null;
    console.log('[Dashboard] YouTube results applied to state', {
      term: lastSearchMeta.term,
      count: searchResults.length,
      elapsedFromStartMs: elapsedFromStart,
      receivedToRenderMs: renderLatency,
    });
  }, [searchResults, lastSearchMeta]);

  const addToPlaylist = async () => {
    if (sourceTab === 'youtube' && selectedYouTubePlaylistId) {
      if (!siteId) return;
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await fetch(
          `/api/youtube/playlist-items?site=${encodeURIComponent(siteId)}&playlistId=${encodeURIComponent(selectedYouTubePlaylistId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.error || 'Failed to load playlist items');
        }
        const data = await response.json();
        const items = Array.isArray(data.items) ? data.items : [];
        const newVideos = items.map((item) => ({
          content_id: item.videoId,
          content_type: 'youtube',
          title: decodeHtmlEntities(item.title),
          thumbnail_url: item.preview_api_url || item.thumbnail_url,
          duration: null,
        }));
        if (!newVideos.length) return;
        if (fallbackMode && onAddToFallback) {
          onAddToFallback(newVideos, selectedTimeOfDay);
        } else {
          const updated = [...playlist, ...newVideos];
          setPlaylist(updated);
          if (onUpdatePlaylist) {
            onUpdatePlaylist(updated);
          }
        }
        setSelectedYouTubePlaylistId(null);
      } catch (error) {
        console.error('Failed to add YouTube playlist items', error);
        alert('Failed to load playlist items.');
      }
      return;
    }

    if (sourceTab === 'youtube' && selectedCategory === 'channel' && selectedChannelPlaylistId) {
      if (!siteId) return;
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await fetch(
          `/api/youtube/playlist-items?site=${encodeURIComponent(siteId)}&playlistId=${encodeURIComponent(selectedChannelPlaylistId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.error || 'Failed to load playlist items');
        }
        const data = await response.json();
        const items = Array.isArray(data.items) ? data.items : [];
        const newVideos = items.map((item) => ({
          content_id: item.videoId,
          content_type: 'youtube',
          title: decodeHtmlEntities(item.title),
          thumbnail_url: item.preview_api_url || item.thumbnail_url,
          duration: null,
        }));
        if (!newVideos.length) return;
        if (fallbackMode && onAddToFallback) {
          onAddToFallback(newVideos, selectedTimeOfDay);
        } else {
          const updated = [...playlist, ...newVideos];
          setPlaylist(updated);
          if (onUpdatePlaylist) {
            onUpdatePlaylist(updated);
          }
        }
        setSelectedChannelPlaylistId(null);
      } catch (error) {
        console.error('Failed to add channel playlist items', error);
        alert('Failed to load playlist items.');
      }
      return;
    }

    if (sourceTab === 'playlists') {
      const selectedPlaylist = savedPlaylists.find((p) => p.id === selectedSavedPlaylistId);
      if (!selectedPlaylist) return;
      const items = selectedPlaylist.items || [];
      if (!items.length) return;
      if (fallbackMode && onAddToFallback) {
        onAddToFallback(items, selectedTimeOfDay);
      } else {
        const updated = [...playlist, ...items];
        setPlaylist(updated);
        if (adHocMode && onUpdatePlaylist) {
          onUpdatePlaylist(updated);
        }
      }
      return;
    }

    if (sourceTab === 'saved') {
      if (!selectedSavedAssets.length) return;
      const newAssets = selectedSavedAssets
        .map((assetId) => {
          const asset = savedAssets.find((a) => a.id === assetId);
          if (!asset) return null;
          const rawDuration =
            savedAssetDurations[asset.id] ??
            asset.default_duration ??
            30;
          const parsedDuration = parseInt(rawDuration, 10);
          const normalizedDuration = Math.min(
            Math.max(Number.isNaN(parsedDuration) ? 30 : parsedDuration, 5),
            600
          );
          return {
            content_id: asset.id,
            content_type: asset.content_type,
            title: asset.title,
            thumbnail_url: asset.preview_api_url || asset.thumbnail_url || asset.thumbnail_api_url,
            thumbnail_api_url: asset.preview_api_url || asset.thumbnail_api_url || null,
            duration: asset.content_type === 's3_image' ? normalizedDuration : null,
          };
        })
        .filter(Boolean);
      if (!newAssets.length) return;
      if (fallbackMode && onAddToFallback) {
        onAddToFallback(newAssets, selectedTimeOfDay);
      } else {
        const updated = [...playlist, ...newAssets];
        setPlaylist(updated);
        if (adHocMode && onUpdatePlaylist) {
          onUpdatePlaylist(updated);
        }
      }
      setSelectedSavedAssets([]);
      return;
    }

    if (selectedSearchResults.length > 0) {
      const baseResults = selectedCategory === 'channel' ? channelVideos : searchResults;
      const newVideos = selectedSearchResults.map((index) => {
        const video = baseResults[index];
        return {
          content_id: video.id?.videoId || video.videoId || video.id,
          content_type: 'youtube',
          title: decodeHtmlEntities(video.snippet.title),
          thumbnail_url: video.snippet.thumbnails.default.url,
          duration: null,
        };
      });
      if (fallbackMode && onAddToFallback) {
        onAddToFallback(newVideos, selectedTimeOfDay);
      } else {
        const updated = [...playlist, ...newVideos];
        setPlaylist(updated);
        if (adHocMode && onUpdatePlaylist) {
          onUpdatePlaylist(updated);
        }
      }
      setSelectedSearchResults([]);
    }
  };

  const loadToUpNext = () => {
    if (sourceTab !== 'youtube') return;
    if (!onLoadToUpNext) return;
    if (selectedSearchResults.length > 0) {
      const baseResults = selectedCategory === 'channel' ? channelVideos : searchResults;
      const video = baseResults[selectedSearchResults[0]];
      const videoData = {
        content_id: video.id?.videoId || video.videoId || video.id,
        content_type: 'youtube',
        title: decodeHtmlEntities(video.snippet.title),
        thumbnail_url: video.snippet.thumbnails.default.url,
      };
      onLoadToUpNext(videoData);
      setSelectedSearchResults([]);
    }
  };

  const handleSelectSearchResult = (index) => {
    if (sourceTab === 'youtube' && selectedCategory === 'channel') {
      setSelectedChannelPlaylistId(null);
    }
    setSelectedSearchResults((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSelectSavedAsset = (assetId) => {
    setSelectedSavedAssets((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    );
  };

  const handleSelectSavedPlaylistTab = (playlistId) => {
    setSelectedSavedPlaylistId((prev) => (prev === playlistId ? null : playlistId));
  };

  const handleSelectYouTubePlaylist = (playlistId) => {
    setSelectedYouTubePlaylistId((prev) => (prev === playlistId ? null : playlistId));
  };

  const handleSelectChannelPlaylist = (playlistId) => {
    setSelectedSearchResults([]);
    setSelectedChannelPlaylistId((prev) => (prev === playlistId ? null : playlistId));
  };

  const handleChannelTabChange = (tab) => {
    setChannelResultsTab(tab);
    setSelectedSearchResults([]);
    setSelectedChannelPlaylistId(null);
  };

  const handleSavedAssetDurationChange = (assetId, value) => {
    setSavedAssetDurations((prev) => ({
      ...prev,
      [assetId]: value,
    }));
  };

  const handlePlaylistItemDurationChange = (index, value) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed <= 0) return;
    const clamped = Math.min(Math.max(parsed, 5), 600);
    const updatedPlaylist = [...playlist];
    updatedPlaylist[index] = {
      ...updatedPlaylist[index],
      duration: clamped,
    };
    setPlaylist(updatedPlaylist);
    if (adHocMode && onUpdatePlaylist) {
      onUpdatePlaylist(updatedPlaylist);
    }
  };

  const getContentTypeLabel = (item = {}) => {
    return resolveContentTypeMeta(item).label;
  };

  const getContentTypeClass = (item = {}) => {
    return resolveContentTypeMeta(item).badgeClass;
  };

  const renderThumbnail = (thumbSrc, title, className = '', contentType = '') => {
    const trimmedClassName = className.trim();
    if (thumbSrc) {
      if (contentType === 's3_video') {
        return (
          <video
            src={thumbSrc}
            className={trimmedClassName}
            muted
            playsInline
            preload='metadata'
          />
        );
      }
      return <img src={thumbSrc} alt={title} className={trimmedClassName} />;
    }
    const fallbackGlyph = contentType === 's3_audio' ? '🎵' : title ? title.charAt(0) : 'A';
    return (
      <div className={`saved-asset-fallback-thumb ${trimmedClassName}`.trim()}>
        {fallbackGlyph}
      </div>
    );
  };

  const removeSelectedFromPlaylist = () => {
    if (selectedPlaylistItem !== null) {
      const updatedPlaylist = playlist.filter((_, index) => index !== selectedPlaylistItem);
      setPlaylist(updatedPlaylist);
      setSelectedPlaylistItem(null);
      if (adHocMode && onUpdatePlaylist) {
        onUpdatePlaylist(updatedPlaylist);
      }
    }
  };

  const moveItemUp = () => {
    if (selectedPlaylistItem > 0) {
      const updatedPlaylist = [...playlist];
      const temp = updatedPlaylist[selectedPlaylistItem - 1];
      updatedPlaylist[selectedPlaylistItem - 1] = updatedPlaylist[selectedPlaylistItem];
      updatedPlaylist[selectedPlaylistItem] = temp;
      setPlaylist(updatedPlaylist);
      setSelectedPlaylistItem(selectedPlaylistItem - 1);
      if (adHocMode && onUpdatePlaylist) {
        onUpdatePlaylist(updatedPlaylist);
      }
    }
  };

  const moveItemDown = () => {
    if (selectedPlaylistItem < playlist.length - 1) {
      const updatedPlaylist = [...playlist];
      const temp = updatedPlaylist[selectedPlaylistItem + 1];
      updatedPlaylist[selectedPlaylistItem + 1] = updatedPlaylist[selectedPlaylistItem];
      updatedPlaylist[selectedPlaylistItem] = temp;
      setPlaylist(updatedPlaylist);
      setSelectedPlaylistItem(selectedPlaylistItem + 1);
      if (adHocMode && onUpdatePlaylist) {
        onUpdatePlaylist(updatedPlaylist);
      }
    }
  };

  const handleSavePlaylist = () => {
    if (!onSavePlaylist) return;
    if (playlist.length === 0) {
      alert('Cannot save an empty playlist.');
      return;
    }
    setSaveAsName(playlistName && playlistName !== 'New Playlist' ? playlistName : '');
    setShowSaveAsDialog(true);
  };

  const handleUpdatePlaylist = () => {
    if (!onUpdatePlaylist) return;
    onUpdatePlaylist(playlist);
  };

  const confirmSaveAs = () => {
    if (!onSavePlaylist) return;
    const name = (saveAsName || '').trim();
    if (!name) {
      alert('Please enter a playlist name.');
      return;
    }
    onSavePlaylist(playlist, name);
    setShowSaveAsDialog(false);
  };

  const handleDragStart = (index) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (index) => {
    if (draggedItemIndex === null) return;
    if (draggedItemIndex !== index) {
      const updatedPlaylist = [...playlist];
      const draggedItem = updatedPlaylist.splice(draggedItemIndex, 1)[0];
      updatedPlaylist.splice(index, 0, draggedItem);
      setPlaylist(updatedPlaylist);
      setDraggedItemIndex(index);
      if (adHocMode && onUpdatePlaylist) {
        onUpdatePlaylist(updatedPlaylist);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  const handleOpenPopup = () => {
    setShowPlaylistPopup(true);
  };

  const handleSelectSavedPlaylistPopup = (p) => {
    if (onLoadSavedPlaylist) {
      onLoadSavedPlaylist(p);
    }
    setShowPlaylistPopup(false);
  };

  const handleSourceChange = (tab) => {
    setSourceTab(tab);
    setShowPlaylistPopup(false);
    if (tab === 'youtube') {
      setSelectedSavedAssets([]);
      setSelectedSavedPlaylistId(null);
      setSelectedYouTubePlaylistId(null);
      setSelectedChannelPlaylistId(null);
    } else if (tab === 'saved') {
      setSelectedSearchResults([]);
      setSelectedSavedPlaylistId(null);
      setSelectedYouTubePlaylistId(null);
      setSelectedChannelPlaylistId(null);
    } else if (tab === 'playlists') {
      setSelectedSearchResults([]);
      setSelectedSavedAssets([]);
      setSelectedYouTubePlaylistId(null);
      setSelectedChannelPlaylistId(null);
    } else if (tab === 'overlay-ads') {
      setSelectedSearchResults([]);
      setSelectedSavedAssets([]);
      setSelectedSavedPlaylistId(null);
      setSelectedYouTubePlaylistId(null);
      setSelectedChannelPlaylistId(null);
    } else if (tab === 'default-ads') {
      setSelectedSearchResults([]);
      setSelectedSavedAssets([]);
      setSelectedSavedPlaylistId(null);
      setSelectedYouTubePlaylistId(null);
      setSelectedChannelPlaylistId(null);
    } else if (tab === 'links') {
      setSelectedSearchResults([]);
      setSelectedSavedAssets([]);
      setSelectedSavedPlaylistId(null);
      setSelectedYouTubePlaylistId(null);
      setSelectedChannelPlaylistId(null);
    } else if (tab === 'iptv') {
      setSelectedSearchResults([]);
      setSelectedSavedAssets([]);
      setSelectedSavedPlaylistId(null);
      if (!iptvResults.length && !iptvLoading) loadIptv(1);
    }
  };

  const isOverlayAdsTab = sourceTab === 'overlay-ads';
  const isDefaultAdsTab = sourceTab === 'default-ads';
  const isSavedAssetsTab = sourceTab === 'saved';
  const isSavedPlaylistsTab = sourceTab === 'playlists';
  const isExternalLinksTab = sourceTab === 'links';
  const isIptvTab = sourceTab === 'iptv';
  const isChannelMode =
    sourceTab === 'youtube'
    && searchProvider === 'youtube'
    && selectedCategory === 'channel';
  const filteredSavedAssets = savedAssets.filter((asset) => {
    const contentType = String(asset?.content_type || '').toLowerCase();
    if (savedAssetTypeFilter === 'all') return true;
    if (savedAssetTypeFilter === 'other') {
      return !['s3_image', 's3_audio', 's3_video'].includes(contentType);
    }
    return contentType === savedAssetTypeFilter;
  });
  const hasSelection =
    !isOverlayAdsTab &&
    !isDefaultAdsTab &&
    ((sourceTab === 'youtube' && selectedSearchResults.length > 0) ||
      (isSavedAssetsTab && selectedSavedAssets.length > 0) ||
      (isSavedPlaylistsTab && !!selectedSavedPlaylistId) ||
      (sourceTab === 'youtube' && !!selectedYouTubePlaylistId) ||
      (isChannelMode && !!selectedChannelPlaylistId));
  const playlistIsEmpty = playlist.length === 0;
  const hasSavedPlaylist = !adHocMode && !!onUpdatePlaylist;
  const addButtonLabel = adHocMode ? 'Add to Ad Hoc Playlist →' : 'Add to Playlist →';
  const youtubeTabLabel = 'Content Search';

  const renderYouTubePlaylistsPanel = () => {
    if (sourceTab !== 'youtube' || searchProvider !== 'youtube') return null;
    return (
      <div className="youtube-playlists-panel">
        <button
          type="button"
          className="youtube-playlists-toggle"
          onClick={() => setYoutubePlaylistsOpen((prev) => !prev)}
        >
          My Playlists
          <span>{youtubePlaylistsOpen ? '▾' : '▸'}</span>
        </button>
        {youtubePlaylistsOpen && (
          <div className="youtube-playlists-content">
            {!youtubeStatus?.connected && (
              <p className="youtube-playlists-note">
                Connect your YouTube account in Settings to access playlists.
              </p>
            )}
            {youtubeStatus?.connected && youtubePlaylistsLoading && (
              <p className="youtube-playlists-note">Loading playlists...</p>
            )}
            {youtubeStatus?.connected && youtubePlaylistsError && (
              <p className="youtube-playlists-note error">{youtubePlaylistsError}</p>
            )}
            {youtubeStatus?.connected && !youtubePlaylistsLoading && !youtubePlaylistsError && (
              youtubePlaylists.length > 0 ? (
                <div className="youtube-playlists-list">
                  {youtubePlaylists.map((playlist) => {
                    const isSelected = selectedYouTubePlaylistId === playlist.id;
                    return (
                      <div
                        key={playlist.id}
                        className={`youtube-playlist-item ${isSelected ? 'selected' : ''}`}
                      >
                        <button
                          type="button"
                          className="youtube-playlist-select"
                          onClick={() => handleSelectYouTubePlaylist(playlist.id)}
                        >
                          <span className="youtube-playlist-title">{playlist.title}</span>
                          <span className="youtube-playlist-count">
                            {playlist.item_count || 0} videos
                          </span>
                        </button>
                        <div className="youtube-target-actions">
                            <button
                              type="button"
                              onClick={() => saveYouTubeTarget({
                                contentId: playlist.id,
                                title: playlist.title,
                                kind: 'playlist',
                                execute: true,
                              })}
                              disabled={
                                !youtubeDeviceProfile
                                || Boolean(youtubeTargetBusy)
                                || youtubeSeedLaunchUnsupported
                              }
                            >
                              Play in YouTube app
                            </button>
                            <button
                              type="button"
                              onClick={() => playPlaylistInSuitestream(playlist.id)}
                              disabled={!onPlayInSuitestream || Boolean(youtubeTargetBusy)}
                            >
                              Play in Suitestream
                            </button>
                            <button
                              type="button"
                              onClick={() => saveYouTubeTarget({
                                contentId: playlist.id,
                                title: playlist.title,
                                kind: 'playlist',
                                execute: false,
                              })}
                              disabled={!youtubeDeviceProfile || Boolean(youtubeTargetBusy)}
                            >
                              Save to Area
                            </button>
                          </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="youtube-playlists-note">No playlists found.</p>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="media-manager">
      {playlistName && <h3>{playlistName}</h3>}

      <div className="media-manager-controls">
        <div
          className={`media-manager-search ${fallbackMode ? 'fallback-mode' : ''} ${
            showPlaylist ? '' : 'full-width'
          }`}
        >
          <div className="media-source-tabs">
            <button
              className={sourceTab === 'youtube' ? 'active' : ''}
              onClick={() => handleSourceChange('youtube')}
            >
              {youtubeTabLabel}
            </button>
            <button
              className={sourceTab === 'saved' ? 'active' : ''}
              onClick={() => handleSourceChange('saved')}
            >
              Saved Assets
            </button>
            <button
              className={sourceTab === 'playlists' ? 'active' : ''}
              onClick={() => handleSourceChange('playlists')}
            >
              Playlists
            </button>
            <button
              className={isExternalLinksTab ? 'active' : ''}
              onClick={() => handleSourceChange('links')}
            >
              External Link
            </button>
            {iptvAvailable && (
              <button
                className={isIptvTab ? 'active' : ''}
                onClick={() => handleSourceChange('iptv')}
              >
                IPTV
              </button>
            )}
            {showOverlayAdsTab && (
              <button
                className={isOverlayAdsTab ? 'active' : ''}
                onClick={() => handleSourceChange('overlay-ads')}
              >
                Overlay Ads
              </button>
            )}
            {showOverlayAdsTab && (
              <button
                className={isDefaultAdsTab ? 'active' : ''}
                onClick={() => handleSourceChange('default-ads')}
              >
                Default Ads
              </button>
            )}
          </div>

          {isOverlayAdsTab ? (
            <div className="overlay-ads-tab">
              <Overlays
                site={site}
                selectedDevice={selectedDevice}
                playbackMode={overlayPlaybackMode}
              />
            </div>
          ) : isDefaultAdsTab ? (
            <div className="overlay-ads-tab">
              <DefaultAds
                site={site}
                selectedDevice={selectedDevice}
                playbackMode={overlayPlaybackMode}
              />
            </div>
          ) : isIptvTab ? (
            <div className="iptv-browser">
              <div className="iptv-browser-heading">
                <div>
                  <h4>Free IPTV channels</h4>
                  <p>Browse live channels from IPTV-org and open a stream in VLC or Kodi.</p>
                </div>
                {iptvTotal > 0 && <span>{iptvTotal.toLocaleString()} streams</span>}
              </div>
              <div className="iptv-browser-filters">
                <label>
                  Country
                  <select
                    value={iptvCountry}
                    onChange={(event) => changeIptvFilter(setIptvCountry, event.target.value)}
                  >
                    <option value="">All countries</option>
                    {iptvCountries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Category
                  <select
                    value={iptvCategory}
                    onChange={(event) => changeIptvFilter(setIptvCategory, event.target.value)}
                  >
                    <option value="">All categories</option>
                    {iptvCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="iptv-search-field">
                  Channel search
                  <input
                    type="text"
                    value={iptvQuery}
                    onChange={(event) => changeIptvFilter(setIptvQuery, event.target.value)}
                    onKeyPress={(event) => event.key === 'Enter' && !iptvLoading && loadIptv(1)}
                    placeholder="Channel, network, country..."
                  />
                </label>
                <button type="button" className="iptv-browse" onClick={() => loadIptv(1)} disabled={iptvLoading}>
                  {iptvLoading ? 'Loading...' : 'Browse'}
                </button>
              </div>
              {iptvMessage && <p className="youtube-target-note">{iptvMessage}</p>}
              {iptvLoading && iptvResults.length === 0 && (
                <p className="youtube-target-note">
                  Loading countries and live channels…
                </p>
              )}
              <div className="iptv-results">
                {iptvResults.map((stream) => (
                  <article className="iptv-result" key={stream.id}>
                    <div className="iptv-result-logo">
                      {stream.logo ? (
                        <img src={stream.logo} alt="" loading="lazy" />
                      ) : (
                        <span>{stream.countryFlag || 'TV'}</span>
                      )}
                    </div>
                    <div className="iptv-result-details">
                      <strong>{stream.title}</strong>
                      <span>
                        {stream.countryFlag} {stream.countryName}
                        {stream.quality ? ` · ${stream.quality}` : ''}
                        {stream.label ? ` · ${stream.label}` : ''}
                      </span>
                      {stream.categoryNames?.length > 0 && (
                        <small>{stream.categoryNames.join(' · ')}</small>
                      )}
                    </div>
                    <div className="iptv-result-actions">
                      {iptvProfiles.map((profile) => (
                        <button
                          type="button"
                          key={profile.id}
                          className={`iptv-play iptv-play--${profile.adapterKey}`}
                          onClick={() => playIptvStream(stream, profile)}
                          disabled={Boolean(iptvBusy)}
                        >
                          Play in {profile.adapterKey === 'vlc' ? 'VLC' : 'Kodi'}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="iptv-link-tools"
                        onClick={() => useProviderSearchResult({
                          title: stream.title,
                          url: stream.url,
                          playable: true,
                        })}
                        disabled={Boolean(iptvBusy)}
                      >
                        Open link tools
                      </button>
                    </div>
                  </article>
                ))}
              </div>
              {iptvHasMore && (
                <button
                  type="button"
                  className="iptv-load-more"
                  onClick={() => loadIptv(iptvPage + 1, true)}
                  disabled={iptvLoading}
                >
                  {iptvLoading ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          ) : isExternalLinksTab ? (
            <div className="external-media-link-panel">
              <h4>Open a link in an installed app</h4>
              <p>
                Paste a supported YouTube, Spotify, Twitch, SoundCloud, or TuneIn link.
                With VLC or Kodi selected, you can also paste an HTTP, HTTPS, or RTSP
                network stream or playlist. Suitestream.tv validates it before anything is sent.
              </p>
              <label className="external-media-link-label" htmlFor="external-media-link-url">Media URL or stream address</label>
              <div className="external-media-link-input">
                <input
                  id="external-media-link-url"
                  type="text"
                  value={externalLinkInput}
                  onChange={(event) => {
                    setExternalLinkInput(event.target.value);
                    setExternalLinkPreview(null);
                    setExternalLinkMessage('');
                  }}
                  onKeyPress={(event) => event.key === 'Enter' && previewExternalLink()}
                  placeholder="https://open.spotify.com/track/..."
                  disabled={Boolean(externalLinkBusy)}
                />
                <button
                  type="button"
                  onClick={previewExternalLink}
                  disabled={!externalLinkInput.trim() || Boolean(externalLinkBusy)}
                >
                  Check link
                </button>
              </div>
              {externalLinkPreview && (
                <div className="external-media-link-preview">
                  <strong>{externalLinkPreview.title}</strong>
                  <span>
                    {externalLinkPreview.provider} · {externalLinkPreview.providerKind}
                  </span>
                  <code>{externalLinkPreview.canonicalUrl}</code>
                  {externalLinkProfiles.length > 1 && (
                    <label className="external-link-player-select">
                      Open with
                      <select
                        value={externalLinkProfile?.id || ''}
                        onChange={(event) => setExternalLinkProfileId(event.target.value)}
                      >
                        {externalLinkProfiles.map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {profile.displayName || profile.adapterKey}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <div className="youtube-target-actions">
                    {adHocMode
                      && externalLinkPreview.provider === 'youtube'
                      && externalLinkPreview.queueSupported ? (
                        <button
                          type="button"
                          onClick={addExternalLinkToAdHoc}
                          disabled={Boolean(externalLinkBusy)}
                        >
                          {externalLinkBusy === 'ad_hoc'
                            ? 'Adding...'
                            : 'Add to Ad Hoc Playlist'}
                        </button>
                      ) : null}
                    <button
                      type="button"
                      onClick={() => handleExternalLinkAction('play')}
                      disabled={
                        !externalLinkProfile
                        || Boolean(externalLinkBusy)
                        || externalLinkProfile.capabilities?.launchUri === false
                        || externalLinkProfile.capabilities?.bridge?.launchUri === false
                      }
                    >
                      Play in installed app
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExternalLinkAction('save_area')}
                      disabled={!externalLinkProfile || Boolean(externalLinkBusy)}
                    >
                      {externalLinkBusy === 'save_area' ? 'Saving...' : 'Save to Area'}
                    </button>
                  </div>
                </div>
              )}
              {externalLinkHistory.length > 0 && (
                <div className="external-link-history">
                  <h5>Recently checked links</h5>
                  {externalLinkHistory.map((entry) => (
                    <div className="external-link-history-row" key={entry.url}>
                      <button
                        type="button"
                        className="external-link-history-reuse"
                        onClick={() => reuseExternalLink(entry)}
                      >
                        <strong>{entry.title || entry.url}</strong>
                        <span>{entry.provider} · {entry.providerKind}</span>
                      </button>
                      <button
                        type="button"
                        className="external-link-history-delete"
                        onClick={() => deleteExternalLinkHistoryEntry(entry.url)}
                        aria-label={`Delete ${entry.title || entry.url} from recent links`}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {externalLinkMessage && (
                <p className="youtube-target-note">{externalLinkMessage}</p>
              )}
            </div>
          ) : (
            <>
              {!isSavedAssetsTab && !isSavedPlaylistsTab && (
                <>
                  <fieldset className="provider-search-chooser">
                    <legend>Search service</legend>
                    {providerSearchOptions.map((option) => (
                      <label key={option.key}>
                        <input
                          type="radio"
                          name="content-search-provider"
                          checked={searchProvider === option.key}
                          onChange={() => {
                            setSearchProvider(option.key);
                            setProviderSearchResults([]);
                            setProviderSearchMessage('');
                            setProviderSearchFallbackUrl('');
                          }}
                        />
                        {option.label}
                      </label>
                    ))}
                  </fieldset>
                  {searchProvider === 'youtube' && youtubeStatus?.connected && (
                    <p className="youtube-connection-note">
                      Using your connected YouTube account for search.
                    </p>
                  )}
                  <div className="search-bar">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={`Search ${
                        providerSearchOptions.find((option) => option.key === searchProvider)?.label
                        || 'content'
                      }...`}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button onClick={handleSearch}>Search</button>
                  </div>

                  {searchProvider === 'youtube' && <div className="filters-row">
                    <div className="category-filters">
                      {categoryOptions.map((category) => (
                        <button
                          key={category}
                          onClick={() => !categoryFixed && setSelectedCategory(category)}
                          className={`${selectedCategory === category ? 'active' : ''} ${category === 'channel' ? 'channel' : ''}`.trim()}
                          disabled={categoryFixed}
                        >
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </button>
                      ))}
                    </div>
                    {!['channel', 'playlists'].includes(selectedCategory) && (
                      <div className="length-filter">
                        <label>
                          Length:
                          <select
                            value={selectedLength}
                            onChange={(e) => setSelectedLength(e.target.value)}
                          >
                            <option value="any">Any</option>
                            <option value="short">Short (&lt;4 min)</option>
                            <option value="medium">Medium (4-20 min)</option>
                            <option value="long">Long (&gt;20 min)</option>
                          </select>
                        </label>
                      </div>
                    )}
                  </div>}
                </>
              )}

              {isSavedAssetsTab && (
                <>
                  <p className="saved-assets-copy">
                    Select uploaded images, videos, or audio from your Saved Assets library. Images can
                    have a custom duration before the next item fades in.
                  </p>
                  <div className="saved-assets-filter-row">
                    <div className="chooser-filters" aria-label="Saved asset type filter">
                      {SAVED_ASSET_TYPE_FILTERS.map((filter) => (
                        <button
                          key={filter.value}
                          type="button"
                          className={savedAssetTypeFilter === filter.value ? 'active' : ''}
                          onClick={() => setSavedAssetTypeFilter(filter.value)}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                    <span className="saved-assets-count">
                      {filteredSavedAssets.length} shown / {savedAssets.length} saved
                    </span>
                  </div>
                </>
              )}
              {isSavedPlaylistsTab && (
                <p className="saved-assets-copy">
                  Choose one of your saved playlists and add all of its items at once.
                </p>
              )}

              <div className="search-actions">
                {fallbackMode && (
                  <select
                    value={selectedTimeOfDay}
                    onChange={(e) => setSelectedTimeOfDay(e.target.value)}
                  >
                    <option value="Morning">Morning Playlist</option>
                    <option value="Afternoon">Afternoon Playlist</option>
                    <option value="Evening">Evening Playlist</option>
                  </select>
                )}
                {!showPlaylist && !isOverlayAdsTab && (
                  <>
                    <button
                      onClick={addToPlaylist}
                      disabled={!hasSelection}
                      className="add-to-playlist-button"
                    >
                      {adHocMode ? 'Add to Ad Hoc Playlist' : 'Add to Playlist'}
                    </button>
                    {renderYouTubePlaylistsPanel()}
                    {showLoadToUpNextButton && !fallbackMode && !adHocMode && !isSavedAssetsTab && (
                      <button
                        onClick={loadToUpNext}
                        disabled={selectedSearchResults.length === 0}
                        className="load-to-upnext-button"
                      >
                        Load to Up Next
                      </button>
                    )}
                  </>
                )}
              </div>

              <h4>
                {isSavedAssetsTab
                  ? 'Saved Assets'
                  : isSavedPlaylistsTab
                  ? 'Saved Playlists'
                  : 'Search Results'}
              </h4>
              <div className="search-results-container">
                {isSavedAssetsTab ? (
                  savedAssetsLoading ? (
                    <p>Loading saved assets...</p>
                  ) : filteredSavedAssets.length > 0 ? (
                    <div className="saved-assets-list">
                      {filteredSavedAssets.map((asset) => {
                        const isSelected = selectedSavedAssets.includes(asset.id);
                        return (
                          <div
                            key={asset.id}
                            className={`saved-asset-row ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectSavedAsset(asset.id)}
                          >
                            <div className="saved-asset-meta">
                              <div className="saved-asset-title">
                                <span>{asset.title}</span>
                                <span className={`asset-type-badge ${getContentTypeClass(asset)}`}>
                                  {getContentTypeLabel(asset).replace('Saved ', '')}
                                </span>
                              </div>
                              {asset.content_type === 's3_image' && (
                                <label
                                  className="duration-input"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Duration (s):
                                  <input
                                    type="number"
                                    min="5"
                                    max="600"
                                    value={
                                      savedAssetDurations[asset.id] ??
                                      asset.default_duration ??
                                      30
                                    }
                                    onChange={(e) =>
                                      handleSavedAssetDurationChange(asset.id, e.target.value)
                                    }
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p>
                      {savedAssets.length
                        ? 'No saved assets match this filter.'
                        : 'No saved assets found. Upload content via Creative Studio.'}
                    </p>
                  )
                ) : isSavedPlaylistsTab ? (
                  savedPlaylists.length > 0 ? (
                    <div className="saved-playlists-list">
                      {savedPlaylists.map((p) => {
                        const isSelected = selectedSavedPlaylistId === p.id;
                        return (
                          <div
                            key={p.id}
                            className={`saved-playlist-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectSavedPlaylistTab(p.id)}
                          >
                            <div className="saved-playlist-title-row">
                              <span>{p.name}</span>
                              <span className="saved-playlist-count">{p.tracksCount || (p.items ? p.items.length : 0)} tracks</span>
                            </div>
                            <div className="saved-playlist-preview">
                              {(p.items || []).slice(0, 3).map((item, idx) => (
                                <span key={idx} className={`asset-type-badge ${getContentTypeClass(item)}`}>
                                  {getContentTypeLabel(item)}
                                </span>
                              ))}
                              {(p.items || []).length > 3 && <span className="saved-playlist-more">+{(p.items || []).length - 3} more</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p>No saved playlists found.</p>
                  )
                ) : isChannelMode ? (
                  <>
                    <div className="channel-results-header">
                      <div className="channel-results-title">
                        {channelMeta?.title || 'Channel Results'}
                      </div>
                      <div className="channel-results-tabs">
                        <button
                          type="button"
                          className={channelResultsTab === 'videos' ? 'active' : ''}
                          onClick={() => handleChannelTabChange('videos')}
                        >
                          Videos
                        </button>
                        <button
                          type="button"
                          className={channelResultsTab === 'playlists' ? 'active' : ''}
                          onClick={() => handleChannelTabChange('playlists')}
                        >
                          Playlists
                        </button>
                      </div>
                    </div>
                    {channelLoading && <p className="channel-results-note">Loading channel results...</p>}
                    {channelError && <p className="channel-results-note error">{channelError}</p>}
                    {!channelLoading && !channelError && channelResultsTab === 'videos' && (
                      channelVideos.length > 0 ? (
                        <>
                          <div className="search-results">
                            {channelVideos.map((video, index) => {
                              const contentId = getYouTubeVideoId(video);
                              return (
                              <div
                                key={`${video.videoId || index}`}
                                className={`search-item ${
                                  selectedSearchResults.includes(index) ? 'selected' : ''
                                }`}
                                onClick={() => handleSelectSearchResult(index)}
                              >
                                <img
                                  src={video.snippet.thumbnails.default.url}
                                  alt={decodeHtmlEntities(video.snippet.title)}
                                />
                                <span>{decodeHtmlEntities(video.snippet.title)}</span>
                                <div className="youtube-target-actions">
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      saveYouTubeTarget({
                                        contentId,
                                        title: video.snippet.title,
                                        kind: 'video',
                                        execute: true,
                                      });
                                    }}
                                    disabled={
                                      !youtubeDeviceProfile
                                      || !contentId
                                      || Boolean(youtubeTargetBusy)
                                      || youtubeSeedLaunchUnsupported
                                    }
                                  >
                                    Play in YouTube app
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      playVideoInSuitestream(video);
                                    }}
                                    disabled={!onPlayInSuitestream || !contentId}
                                  >
                                    Play in Suitestream
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      saveYouTubeTarget({
                                        contentId,
                                        title: video.snippet.title,
                                        kind: 'video',
                                        execute: false,
                                      });
                                    }}
                                    disabled={!youtubeDeviceProfile || !contentId || Boolean(youtubeTargetBusy)}
                                  >
                                    Save to Area
                                  </button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                          {channelVideosNextToken && (
                            <button
                              type="button"
                              className="channel-next-button"
                              onClick={() => loadMoreChannelResults('videos')}
                              disabled={channelLoading}
                            >
                              Next
                            </button>
                          )}
                        </>
                      ) : (
                        <p className="channel-results-note">No videos found for this channel.</p>
                      )
                    )}
                    {!channelLoading && !channelError && channelResultsTab === 'playlists' && (
                      channelPlaylists.length > 0 ? (
                        <>
                          <div className="channel-playlists-list">
                            {channelPlaylists.map((playlist) => {
                              const isSelected = selectedChannelPlaylistId === playlist.id;
                              return (
                                <div
                                  key={playlist.id}
                                  className={`channel-playlist-item ${isSelected ? 'selected' : ''}`}
                                >
                                  <button
                                    type="button"
                                    className="youtube-playlist-select"
                                    onClick={() => handleSelectChannelPlaylist(playlist.id)}
                                  >
                                    <span className="channel-playlist-title">{playlist.title}</span>
                                    <span className="channel-playlist-count">
                                      {playlist.item_count || 0} videos
                                    </span>
                                  </button>
                                  <div className="youtube-target-actions">
                                    <button
                                      type="button"
                                      onClick={() => saveYouTubeTarget({
                                        contentId: playlist.id,
                                        title: playlist.title,
                                        kind: 'playlist',
                                        execute: true,
                                      })}
                                      disabled={
                                        !youtubeDeviceProfile
                                        || Boolean(youtubeTargetBusy)
                                        || youtubeSeedLaunchUnsupported
                                      }
                                    >
                                      Play in YouTube app
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => playPlaylistInSuitestream(playlist.id)}
                                      disabled={!onPlayInSuitestream || Boolean(youtubeTargetBusy)}
                                    >
                                      Play in Suitestream
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => saveYouTubeTarget({
                                        contentId: playlist.id,
                                        title: playlist.title,
                                        kind: 'playlist',
                                        execute: false,
                                      })}
                                      disabled={!youtubeDeviceProfile || Boolean(youtubeTargetBusy)}
                                    >
                                      Save to Area
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {channelPlaylistsNextToken && (
                            <button
                              type="button"
                              className="channel-next-button"
                              onClick={() => loadMoreChannelResults('playlists')}
                              disabled={channelLoading}
                            >
                              Next
                            </button>
                          )}
                        </>
                      ) : (
                        <p className="channel-results-note">No playlists found for this channel.</p>
                      )
                    )}
                  </>
                ) : searchProvider !== 'youtube' ? (
                  <>
                    {providerSearchMessage && (
                      <p className="youtube-target-note">{providerSearchMessage}</p>
                    )}
                    {providerSearchFallbackUrl && (
                      <p className="youtube-target-note">
                        <a href={providerSearchFallbackUrl} target="_blank" rel="noreferrer">
                          Continue this search on DuckDuckGo
                        </a>
                        {' '}then paste a result into External Link.
                      </p>
                    )}
                    {providerSearchResults.length > 0 ? (
                      <div className="provider-search-results">
                        {providerSearchResults.map((result) => (
                          <div className="provider-search-result" key={result.url}>
                            {result.thumbnail && (
                              <img src={result.thumbnail} alt="" loading="lazy" />
                            )}
                            <div>
                              <strong>{result.title}</strong>
                              {result.snippet && <p>{result.snippet}</p>}
                              <code>{result.url}</code>
                            </div>
                            <button
                              type="button"
                              onClick={() => useProviderSearchResult(result)}
                              disabled={Boolean(externalLinkBusy)}
                            >
                              {result.playable ? 'Use stream' : 'Check link'}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : !providerSearchMessage && (
                      <p>Search for content from the selected service.</p>
                    )}
                  </>
                ) : searchResults.length > 0 ? (
                  <>
                    {selectedDevice && !youtubeDeviceProfile && (
                      <p className="youtube-target-note">
                        Configure the installed YouTube app in Apps to enable device playback.
                      </p>
                    )}
                    {youtubeDeviceProfile && youtubeSeedLaunchUnsupported && (
                      <p className="youtube-target-note">
                        Install the latest Suitestream Android app to enable seeded playback.
                      </p>
                    )}
                    {youtubeTargetMessage && (
                      <p className="youtube-target-note">{youtubeTargetMessage}</p>
                    )}
                    <div className="search-results">
                    {searchResults.map((video, index) => {
                      const isPlaylistResult = selectedCategory === 'playlists';
                      const contentId = isPlaylistResult
                        ? getYouTubePlaylistId(video)
                        : getYouTubeVideoId(video);
                      const targetKind = isPlaylistResult
                        ? 'playlist'
                        : limitSearchToLive || selectedCategory === 'live'
                          ? 'stream'
                          : 'video';
                      return (
                      <div
                        key={index}
                        className={`search-item ${
                          !isPlaylistResult && selectedSearchResults.includes(index) ? 'selected' : ''
                        }`}
                        onClick={
                          isPlaylistResult ? undefined : () => handleSelectSearchResult(index)
                        }
                      >
                        <div className="search-item-content">
                          <span className="search-item-title">
                            {decodeHtmlEntities(video.snippet.title)}
                          </span>
                          <div className="youtube-target-actions">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                saveYouTubeTarget({
                                  contentId,
                                  title: video.snippet.title,
                                  kind: targetKind,
                                  execute: true,
                                });
                              }}
                              disabled={
                                !youtubeDeviceProfile
                                || !contentId
                                || Boolean(youtubeTargetBusy)
                                || youtubeSeedLaunchUnsupported
                              }
                            >
                              Play in YouTube app
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                addYouTubeTargetToPlaylist({
                                  contentId,
                                  title: video.snippet.title,
                                  kind: targetKind,
                                  video,
                                });
                              }}
                              disabled={!onUpdatePlaylist || !contentId || Boolean(youtubeTargetBusy)}
                            >
                              {youtubeTargetBusy === `playlist:${contentId}`
                                ? 'Loading playlist…'
                                : adHocMode ? 'Add to Ad Hoc Playlist' : 'Add to Playlist'}
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                saveYouTubeTarget({
                                  contentId,
                                  title: video.snippet.title,
                                  kind: targetKind,
                                  execute: false,
                                });
                              }}
                              disabled={!youtubeDeviceProfile || !contentId || Boolean(youtubeTargetBusy)}
                            >
                              Save to Area
                            </button>
                          </div>
                        </div>
                        <img
                          src={video.snippet.thumbnails.default.url}
                          alt={decodeHtmlEntities(video.snippet.title)}
                        />
                      </div>
                      );
                    })}
                    </div>
                  </>
                ) : (
                  <p>
                    Search for and select items from the YouTube results, then click &quot;Add to
                    Playlist&quot; to queue them.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {showPlaylist && !isOverlayAdsTab && !isExternalLinksTab && (
          <div className="media-manager-transfer">
            <button
              onClick={addToPlaylist}
              disabled={!hasSelection}
              className="add-to-playlist-button"
              title={hasSelection ? '' : 'Select items to add'}
            >
              {addButtonLabel}
            </button>
            {renderYouTubePlaylistsPanel()}
            {showLoadToUpNextButton && !fallbackMode && !adHocMode && !isSavedAssetsTab && (
              <button
                onClick={loadToUpNext}
                disabled={selectedSearchResults.length === 0}
                className="load-to-upnext-button"
              >
                Load to Up Next
              </button>
            )}
          </div>
        )}

        {showPlaylist && (
          <div className="media-manager-playlist">
            <div className="playlist-header">
              <h4>Playlist</h4>
              <button className="dark-blue-button" onClick={handleOpenPopup}>
                Open
              </button>
              <button
                className="green-button"
                onClick={handleSavePlaylist}
                disabled={playlistIsEmpty}
              >
                Save As
              </button>
              {hasSavedPlaylist && (
                <button
                  className="green-button"
                  onClick={handleUpdatePlaylist}
                  disabled={playlistIsEmpty}
                >
                  Save
                </button>
              )}
              <button
                className="red-button"
                onClick={onDeletePlaylist}
                disabled={playlistIsEmpty || !onDeletePlaylist || !hasSavedPlaylist}
              >
                Delete
              </button>
              <button
                className="blue-button"
                onClick={() => setPlaylist([])}
                disabled={playlistIsEmpty}
              >
                Clear
              </button>
            </div>
            <div>
              {playlist.length > 0 ? (
                <div className="playlist-items">
                  {playlist.map((video, index) => {
                    const thumbSrc = video.preview_api_url || video.thumbnail_url || video.thumbnail_api_url || '';
                    return (
                      <div
                        key={index}
                        className={`playlist-item ${
                          selectedPlaylistItem === index ? 'selected' : ''
                        }`}
                        onClick={() => setSelectedPlaylistItem(index)}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => {
                          e.preventDefault();
                          handleDragOver(index);
                        }}
                        onDragEnd={handleDragEnd}
                      >
                        {renderThumbnail(
                          thumbSrc,
                          video.title,
                          'playlist-thumb',
                          video.content_type
                        )}
                        <div className="playlist-item-details">
                          <div className="playlist-item-top-row">
                            <span className="playlist-item-title">{video.title}</span>
                            <span className={`asset-type-badge ${getContentTypeClass(video)}`}>
                              {getContentTypeLabel(video)}
                            </span>
                          </div>
                          {video.content_type === 's3_image' && (
                            <label className="duration-input" onClick={(e) => e.stopPropagation()}>
                              Duration (s):
                              <input
                                type="number"
                                min="5"
                                max="600"
                                value={video.duration || 30}
                                onChange={(e) =>
                                  handlePlaylistItemDurationChange(index, e.target.value)
                                }
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>No items in the playlist.</p>
              )}
            </div>
            <div className="device-controls">
              <button
                className="blue-button"
                onClick={moveItemUp}
                disabled={playlistIsEmpty || selectedPlaylistItem === null}
              >
                Move Up
              </button>
              <button
                className="blue-button"
                onClick={moveItemDown}
                disabled={playlistIsEmpty || selectedPlaylistItem === null}
              >
                Move Down
              </button>
              <button
                className="red-button"
                onClick={removeSelectedFromPlaylist}
                disabled={playlistIsEmpty || selectedPlaylistItem === null}
              >
                Remove Selected
              </button>
            </div>
          </div>
        )}
      </div>

      {showSaveAsDialog && (
        <div className="playlist-popup-backdrop" onClick={() => setShowSaveAsDialog(false)}>
          <div className="playlist-popup-content" onClick={(e) => e.stopPropagation()}>
            <h4>Save Playlist As</h4>
            <input
              type="text"
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              placeholder="Playlist name"
              className="playlist-name-input"
              autoFocus
            />
            <div className="playlist-popup-actions">
              <button className="blue-button" onClick={() => setShowSaveAsDialog(false)}>
                Cancel
              </button>
              <button className="green-button" onClick={confirmSaveAs} disabled={playlistIsEmpty}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showPlaylistPopup && (
        <div className="playlist-popup-backdrop" onClick={() => setShowPlaylistPopup(false)}>
          <div className="playlist-popup-content" onClick={(e) => e.stopPropagation()}>
            <h4>Saved Playlists</h4>
            <div className="playlist-popup-list">
              {savedPlaylists.length > 0 ? (
                savedPlaylists.map((p) => (
                  <div
                    key={p.id}
                    className="popup-playlist-item"
                    onClick={() => handleSelectSavedPlaylistPopup(p)}
                  >
                    <span>{p.name}</span>
                    <span>({p.tracksCount} tracks)</span>
                  </div>
                ))
              ) : (
                <p>No saved playlists found.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaManager;
